<template>
  <div class="hotel-amenities-page">
    <vs-card class="amenities-card">
      <div class="page-header">
        <div>
          <h3 class="page-title">Hotel Amenities Management</h3>
          <p class="page-subtitle">Create categories and options, then control visibility using active status.</p>
        </div>
        <div class="category-count-chip">
          {{ categories.length }} categories
        </div>
      </div>

      <div class="add-category-row">
        <vs-input
          v-model="newCategoryName"
          class="w-full"
          placeholder="Create category (e.g. Bathroom)"
        />
        <vs-button color="primary" @click="createCategory">Add Category</vs-button>
      </div>

      <div v-if="loading" class="text-center py-8 text-gray-500 loading-box">
        Loading amenities...
      </div>

      <div v-else-if="categories.length === 0" class="text-center py-8 text-gray-500 loading-box">
        No amenity categories found.
      </div>

      <div v-else class="category-list">
        <div v-for="category in categories" :key="category._id" class="category-card">
          <div class="category-header">
            <div class="category-title-wrap">
              <h4 class="font-semibold text-lg mb-1">{{ category.name }}</h4>
              <p class="text-sm text-gray-600">
                {{ category.is_active ? 'Active category' : 'Inactive category' }}
              </p>
            </div>
            <div class="status-switch-wrap">
              <span class="status-chip" :class="category.is_active ? 'status-active' : 'status-inactive'">
                {{ category.is_active ? 'Active' : 'Inactive' }}
              </span>
              <vs-switch :value="category.is_active" @input="toggleCategory(category, $event)" />
            </div>
          </div>

          <div class="add-option-row">
            <vs-input
              v-model="newOptions[category._id]"
              class="w-full"
              placeholder="Create option inside this category"
            />
            <vs-button color="success" type="border" @click="createOption(category)">Add Option</vs-button>
          </div>

          <div v-if="category.options && category.options.length" class="option-list">
            <div
              v-for="option in category.options"
              :key="option._id"
              class="option-row"
            >
              <div>
                <p class="font-medium mb-1">{{ option.name }}</p>
                <p class="text-xs text-gray-500">Value: {{ option.value }}</p>
              </div>
              <div class="status-switch-wrap">
                <span class="status-chip" :class="option.is_active ? 'status-active' : 'status-inactive'">
                  {{ option.is_active ? 'Active' : 'Inactive' }}
                </span>
                <vs-switch :value="option.is_active" @input="toggleOption(category, option, $event)" />
              </div>
            </div>
          </div>
          <div v-else class="text-sm text-gray-500 empty-option-box">
            No options in this category.
          </div>
        </div>
      </div>
    </vs-card>
  </div>
</template>

<script>
import axios from '@/axios'

export default {
  name: 'HotelAmenitiesSetting',
  data() {
    return {
      loading: false,
      categories: [],
      newCategoryName: '',
      newOptions: {}
    }
  },
  mounted() {
    this.loadCategories()
  },
  methods: {
    async loadCategories() {
      this.loading = true
      try {
        const response = await axios.get('/admin/hotel/amenities/categories')
        if (response && response.data && response.data.success && Array.isArray(response.data.data)) {
          this.categories = response.data.data
          // Initialize newOptions for each category to ensure reactivity
          this.categories.forEach(cat => {
            if (this.newOptions[cat._id] === undefined) {
              this.$set(this.newOptions, cat._id, '')
            }
          })
        } else {
          this.categories = []
        }
      } catch (error) {
        this.categories = []
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load amenity categories',
          color: 'danger'
        })
      } finally {
        this.loading = false
      }
    },
    async createCategory() {
      const name = this.newCategoryName ? this.newCategoryName.trim() : ''
      if (!name) {
        this.$vs.notify({
          title: 'Warning',
          text: 'Category name is required',
          color: 'warning'
        })
        return
      }
      this.$vs.loading()
      try {
        await axios.post('/admin/hotel/amenities/categories', { name })
        this.newCategoryName = ''
        await this.loadCategories()
        this.$vs.notify({
          title: 'Success',
          text: 'Category created successfully',
          color: 'success'
        })
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to create category',
          color: 'danger'
        })
      } finally {
        this.$vs.loading.close()
      }
    },
    async toggleCategory(category, value) {
      try {
        await axios.put(`/admin/hotel/amenities/categories/${  category._id}`, {
          is_active: !!value
        })
        await this.loadCategories()
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update category',
          color: 'danger'
        })
      }
    },
    async createOption(category) {
      const optionName = this.newOptions[category._id] ? this.newOptions[category._id].trim() : ''
      if (!optionName) {
        this.$vs.notify({
          title: 'Warning',
          text: 'Option name is required',
          color: 'warning'
        })
        return
      }
      this.$vs.loading()
      try {
        await axios.post(`/admin/hotel/amenities/categories/${  category._id  }/options`, {
          name: optionName
        })
        this.$set(this.newOptions, category._id, '')
        await this.loadCategories()
        this.$vs.notify({
          title: 'Success',
          text: 'Option added successfully',
          color: 'success'
        })
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to create option',
          color: 'danger'
        })
      } finally {
        this.$vs.loading.close()
      }
    },
    async toggleOption(category, option, value) {
      try {
        await axios.put(`/admin/hotel/amenities/categories/${  category._id  }/options/${  option._id}`, {
          is_active: !!value
        })
        await this.loadCategories()
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to update option',
          color: 'danger'
        })
      }
    }
  }
}
</script>

<style scoped>
.hotel-amenities-page {
  padding: 8px;
}

.amenities-card {
  border-radius: 14px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}

.page-subtitle {
  margin-top: 6px;
  color: #6b7280;
}

.category-count-chip {
  background: #111827;
  color: #fff;
  border-radius: 9999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
}

.add-category-row,
.add-option-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}

.loading-box {
  border: 1px dashed #d1d5db;
  border-radius: 10px;
}

.category-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  background: #fff;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.category-title-wrap {
  min-width: 0;
}

.option-list {
  border-top: 1px solid #f1f5f9;
  margin-top: 8px;
}

.option-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f8fafc;
}

.status-switch-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-chip {
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
}

.status-active {
  background: #dcfce7;
  color: #166534;
}

.status-inactive {
  background: #fee2e2;
  color: #991b1b;
}

.empty-option-box {
  padding: 10px;
  border: 1px dashed #e5e7eb;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .page-header,
  .add-category-row,
  .add-option-row,
  .category-header,
  .option-row {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
